import { type Dispatch, type SetStateAction, useEffect, useState } from "react";
import { type KeyValueStore, readJSON, writeJSON } from "./storage";

/**
 * A useState mirror that persists through a KeyValueStore. The initial value is
 * loaded lazily from storage on first render, so a restored value is present before paint and
 * StrictMode's double-invoked effect just writes that same value straight back - the load can
 * never be clobbered by the first write. Every later change is persisted. `valid` guards a
 * corrupt or stale blob, falling back to `initial` (see readJSON).
 *
 * The `store` must be a stable reference (a module-level singleton or a memoized fake), or the
 * write effect re-runs every render.
 */
export function usePersistentState<T>(
  store: KeyValueStore,
  key: string,
  initial: T,
  valid?: (value: unknown) => value is T,
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => readJSON(store, key, initial, valid));
  useEffect(() => {
    writeJSON(store, key, value);
  }, [store, key, value]);
  return [value, setValue];
}
